import type { ComponentProps } from 'react'
import type { SceneLineMarker } from '@scene'

type Line = ComponentProps<typeof SceneLineMarker>['lines'][number]
type SceneLineMarkerPoint = Line[number]

const toPoint = ({ x, y, z }: SceneLineMarkerPoint): SceneLineMarkerPoint => ({ x, y, z })

/** A polygon as a closed line (its first point repeated at the end), or null below two points. */
export const closedLine = (points: SceneLineMarkerPoint[]): Line | null => {
  if (points.length < 2) return null
  const line = points.map(toPoint)
  if (line.length === 2) return line
  const first = line[0]
  const last = line[line.length - 1]
  const isClosed = first.x === last.x && first.y === last.y && first.z === last.z
  return isClosed ? line : [...line, first]
}

interface AxisElement {
  guid: string
  start: SceneLineMarkerPoint
  end: SceneLineMarkerPoint
}

/**
 * The bar elements as their axis from start to end: beams, purlins, columns,
 * the slab beams of Decken- and Dachpositionen, and the rips and lintels of
 * the walls (by their position guid).
 */
const axisElements = (model: PlanarModel): AxisElement[] => [
  ...[...(model.beams ?? []), ...(model.purlins ?? []), ...(model.columns ?? [])].map(
    ({ guid, shape }) => ({ guid, ...shape }),
  ),
  ...[...(model.vertical_slabs ?? []), ...(model.vertical_roof_slabs ?? [])].flatMap(({ beam }) =>
    beam ? [{ guid: beam.guid, ...beam.shape }] : [],
  ),
  ...[...(model.rips ?? []), ...(model.lintels ?? [])].map(({ position_guid, start, end }) => ({
    guid: position_guid,
    start,
    end,
  })),
]

/**
 * The outlines of the planar elements with these guids. Elements with an
 * area (walls, slabs, roof slabs, Decken- and Dachpositionen) as their shape
 * and the shapes of their openings; bar elements (beams, purlins, columns,
 * slab beams, rips, lintels) as their axis. Unknown guids are skipped.
 */
export const focusOutlines = (model: PlanarModel, guids: string[]): Line[] => {
  if (guids.length === 0) return []
  const wanted = new Set(guids)
  const shapeElements = [
    ...model.walls,
    ...model.slabs,
    ...model.roof_slabs,
    ...(model.vertical_slabs ?? []),
    ...(model.vertical_roof_slabs ?? []),
  ]

  const areaOutlines = shapeElements
    .filter(element => wanted.has(element.guid))
    .flatMap(element => [
      closedLine(element.shape.points),
      ...(element.openings ?? []).map(opening => closedLine(opening.shape.points)),
    ])
  const axes = axisElements(model)
    .filter(element => wanted.has(element.guid))
    .map(({ start, end }) => closedLine([start, end]))

  return [...areaOutlines, ...axes].filter((line): line is Line => line !== null)
}
